import { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { Building2, Crown, Loader2, Mail, Phone, FileText, Plus, Search, UserPlus, Users, Sparkles, Trash2 } from 'lucide-react';

const LOGO = '/imgs/logos allana/letra_A_logo.png';

interface Client {
  id: string;
  name: string;
  email: string;
}

interface OpposingParty {
  id: string;
  name: string;
  email: string;
  phone: string;
  document_number: string;
}

interface AddPartiesModalProps {
  isOpen: boolean;
  onClose: () => void;
  caseId: string;
  onDataUpdate: () => void;
}

const emptyParty = { name: '', email: '', phone: '', document_type: 'CPF', document_number: '', address: '' };

const initials = (name: string) =>
  name.split(/\s+/).filter(Boolean).slice(0, 2).map((p) => p[0]).join('').toUpperCase();

export const AddPartiesModal = ({ isOpen, onClose, caseId, onDataUpdate }: AddPartiesModalProps) => {
  const { toast } = useToast();
  const [activeTab, setActiveTab] = useState<'active' | 'passive'>('active');
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [availableOpposingParties, setAvailableOpposingParties] = useState<OpposingParty[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [search, setSearch] = useState('');
  const [newOpposingParty, setNewOpposingParty] = useState(emptyParty);
  // Parte contrária escrita no cadastro do processo (ex.: "Estado de Pernambuco e outro")
  const [suggestedNames, setSuggestedNames] = useState<string[]>([]);

  const loadAvailableClients = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_available_clients_for_case' as any, { case_id_param: caseId });
    if (error) {
      console.error('Erro ao carregar clientes:', error);
      toast({ title: 'Erro', description: 'Erro ao carregar clientes disponíveis', variant: 'destructive' });
      return;
    }
    setAvailableClients((data as Client[]) || []);
  }, [caseId, toast]);

  const loadAvailableOpposingParties = useCallback(async () => {
    const { data, error } = await supabase.rpc('get_available_opposing_parties_for_case' as any, { case_id_param: caseId });
    if (error) {
      console.error('Erro ao carregar partes contrárias:', error);
      toast({ title: 'Erro', description: 'Erro ao carregar partes contrárias', variant: 'destructive' });
      return;
    }
    setAvailableOpposingParties((data as OpposingParty[]) || []);
  }, [caseId, toast]);

  useEffect(() => {
    if (!isOpen) return;
    setSearch('');
    setNewOpposingParty(emptyParty);
    loadAvailableClients();
    loadAvailableOpposingParties();
    supabase
      .from('cases')
      .select('opposing_party')
      .eq('id', caseId)
      .single()
      .then(({ data }) => {
        const text = String((data as any)?.opposing_party || '');
        const names = text
          .split(/,| e outros? \(\d+\)| e outro$/i)
          .map((n) => n.trim())
          .filter((n) => n.length > 2);
        setSuggestedNames([...new Set(names)]);
      });
  }, [isOpen, caseId, loadAvailableClients, loadAvailableOpposingParties]);

  const handleAddClient = async (clientId: string, isPrimary = false) => {
    setBusyId(`${clientId}-${isPrimary}`);
    try {
      const { error } = await supabase.rpc('add_client_to_case' as any, {
        case_id_param: caseId,
        client_id_param: clientId,
        is_primary_param: isPrimary,
      });
      if (error) throw error;
      toast({ title: 'Cliente adicionado', description: isPrimary ? 'Adicionado como cliente principal.' : 'Adicionado ao processo.' });
      onDataUpdate();
      loadAvailableClients();
    } catch (error: any) {
      console.error('Erro ao adicionar cliente:', error);
      toast({ title: 'Erro', description: error?.message || 'Erro ao adicionar cliente ao processo', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const handleAddOpposingParty = async (opposingPartyId: string, isPrimary = false) => {
    setBusyId(`${opposingPartyId}-${isPrimary}`);
    try {
      const { error } = await supabase.rpc('add_opposing_party_to_case' as any, {
        case_id_param: caseId,
        opposing_party_id_param: opposingPartyId,
        is_primary_param: isPrimary,
      });
      if (error) throw error;
      toast({ title: 'Parte contrária adicionada', description: isPrimary ? 'Adicionada como parte principal.' : 'Adicionada ao processo.' });
      onDataUpdate();
      loadAvailableOpposingParties();
    } catch (error: any) {
      console.error('Erro ao adicionar parte contrária:', error);
      toast({ title: 'Erro', description: error?.message || 'Erro ao adicionar parte contrária ao processo', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const handleCreateOpposingParty = async () => {
    const name = newOpposingParty.name.trim();
    if (!name) {
      toast({ title: 'Informe o nome', description: 'O nome da parte contrária é obrigatório.', variant: 'destructive' });
      return;
    }
    setCreating(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Sessão expirada. Entre novamente.');
      const { data: created, error } = await supabase
        .from('opposing_parties' as any)
        .insert({
          user_id: user.id,
          name,
          email: newOpposingParty.email.trim() || null,
          phone: newOpposingParty.phone.trim() || null,
          document_type: newOpposingParty.document_type,
          document_number: newOpposingParty.document_number.trim() || null,
          address: newOpposingParty.address.trim() || null,
        })
        .select('id')
        .single();
      if (error) throw error;
      const { error: linkError } = await supabase.rpc('add_opposing_party_to_case' as any, {
        case_id_param: caseId,
        opposing_party_id_param: (created as any).id,
        is_primary_param: true,
      });
      if (linkError) throw linkError;
      toast({ title: 'Parte contrária cadastrada', description: `${name} foi adicionada como parte principal.` });
      setNewOpposingParty(emptyParty);
      onDataUpdate();
      loadAvailableOpposingParties();
    } catch (error: any) {
      console.error('Erro ao criar parte contrária:', error);
      toast({ title: 'Erro ao cadastrar', description: error?.message || 'Tente novamente.', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  // Exclui a parte contrária do cadastro (sai também dos processos em que estiver)
  const handleDeleteOpposingParty = async (party: OpposingParty) => {
    const { count } = await supabase
      .from('case_opposing_parties' as any)
      .select('id', { count: 'exact', head: true })
      .eq('opposing_party_id', party.id);
    const uses = count || 0;
    const message = uses > 0
      ? `Excluir "${party.name}"?\n\nEla está em ${uses} processo(s) e será removida deles. Não dá para desfazer.`
      : `Excluir "${party.name}"? Não dá para desfazer.`;
    if (!window.confirm(message)) return;
    setBusyId(`${party.id}-delete`);
    try {
      const { error } = await supabase.from('opposing_parties' as any).delete().eq('id', party.id);
      if (error) throw error;
      toast({ title: 'Parte contrária excluída', description: party.name });
      onDataUpdate();
      loadAvailableOpposingParties();
    } catch (error: any) {
      toast({ title: 'Não foi possível excluir', description: error?.message || 'Tente novamente.', variant: 'destructive' });
    } finally {
      setBusyId(null);
    }
  };

  const term = search.trim().toLowerCase();
  const clients = useMemo(
    () => availableClients.filter((c) => !term || c.name.toLowerCase().includes(term) || c.email?.toLowerCase().includes(term)),
    [availableClients, term]
  );
  const parties = useMemo(
    () => availableOpposingParties.filter((p) => !term || p.name.toLowerCase().includes(term) || p.document_number?.includes(term)),
    [availableOpposingParties, term]
  );

  const fieldLabel = 'block text-sm font-medium text-slate-700';
  const fieldInput = 'w-full rounded-lg border-slate-200 text-sm text-slate-900 shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30';

  const ActionButtons = ({ id, onAdd }: { id: string; onAdd: (id: string, primary: boolean) => void }) => (
    <div className="flex shrink-0 gap-1.5">
      <Button
        size="sm"
        onClick={() => onAdd(id, false)}
        disabled={!!busyId}
        className="h-8 rounded-lg border border-slate-200 bg-white px-2.5 text-[12px] text-slate-700 shadow-none hover:border-[#c4ab83] hover:bg-[#faf7f2]"
      >
        {busyId === `${id}-false` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1 h-3.5 w-3.5 text-[#9a7f52]" />}
        Adicionar
      </Button>
      <Button
        size="sm"
        onClick={() => onAdd(id, true)}
        disabled={!!busyId}
        className="h-8 rounded-lg bg-slate-900 px-2.5 text-[12px] text-white hover:bg-slate-800"
        title="Adicionar como principal"
      >
        {busyId === `${id}-true` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Crown className="mr-1 h-3.5 w-3.5 text-[#e8d5a8]" />}
        Principal
      </Button>
    </div>
  );

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto overflow-x-hidden border border-[#c4ab83]/30 bg-white shadow-2xl">
        <DialogHeader className="relative -m-6 mb-2 overflow-hidden border-b border-[#c4ab83]/30 bg-gradient-to-r from-[#faf7f2] via-white to-[#faf7f2] p-6 sm:px-8">
          <img src={LOGO} alt="" aria-hidden="true" className="pointer-events-none absolute -right-4 top-1/2 h-40 w-40 -translate-y-1/2 select-none object-contain opacity-[0.12]" />
          <div className="relative flex items-center gap-4 text-left">
            <img src={LOGO} alt="Allana Sthéfany" className="h-12 w-12 shrink-0 select-none object-contain opacity-80" />
            <div className="min-w-0">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Processo</p>
              <DialogTitle className="text-2xl font-semibold tracking-tight text-slate-900">Partes do processo</DialogTitle>
              <DialogDescription className="text-sm text-slate-500">Adicione clientes ou partes contrárias.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 pt-4">
          {/* Abas */}
          <div className="grid grid-cols-2 gap-1 rounded-xl bg-slate-100 p-1">
            {[
              { key: 'active' as const, label: 'Clientes', hint: 'polo do cliente', icon: UserPlus, count: availableClients.length },
              { key: 'passive' as const, label: 'Partes contrárias', hint: 'outro polo', icon: Building2, count: availableOpposingParties.length },
            ].map((tab) => {
              const active = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => {
                    setActiveTab(tab.key);
                    setSearch('');
                  }}
                  className={`flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-[13px] transition-colors ${active ? 'bg-white font-semibold text-[#7a6440] shadow-sm' : 'text-slate-500 hover:text-slate-800'}`}
                >
                  <tab.icon className="h-4 w-4" />
                  {tab.label}
                  <span className={`text-[11px] tabular-nums ${active ? 'text-[#9a7f52]' : 'text-slate-400'}`}>{tab.count}</span>
                </button>
              );
            })}
          </div>

          {/* Partes contrárias: cadastro rápido */}
          {activeTab === 'passive' && (
            <section className="rounded-2xl border border-slate-200/80 p-4">
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#9a7f52]">Nova parte contrária</p>

              {suggestedNames.length > 0 && (
                <div className="mt-3 flex flex-wrap items-center gap-1.5">
                  <span className="flex items-center gap-1 text-xs text-slate-500">
                    <Sparkles className="h-3.5 w-3.5 text-[#c4ab83]" /> Do cadastro do processo:
                  </span>
                  {suggestedNames.map((name) => (
                    <button
                      key={name}
                      type="button"
                      onClick={() => setNewOpposingParty((prev) => ({ ...prev, name, document_type: /estado|munic[ií]pio|instituto|banco|ltda|s\.?a\.?|prev|compesa|cebraspe|aocp/i.test(name) ? 'CNPJ' : prev.document_type }))}
                      className="rounded-full border border-[#c4ab83]/40 bg-[#faf7f2] px-2.5 py-0.5 text-[12px] font-medium text-[#7a6440] hover:bg-[#c4ab83]/15"
                    >
                      {name}
                    </button>
                  ))}
                </div>
              )}

              <div className="mt-3 grid grid-cols-1 gap-4 md:grid-cols-2">
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="party-name" className={fieldLabel}>Nome *</Label>
                  <Input id="party-name" value={newOpposingParty.name} onChange={(e) => setNewOpposingParty((p) => ({ ...p, name: e.target.value }))} placeholder="Ex.: Estado de Pernambuco" className={fieldInput} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="party-doc-type" className={fieldLabel}>Tipo de documento</Label>
                  <Select value={newOpposingParty.document_type} onValueChange={(value) => setNewOpposingParty((p) => ({ ...p, document_type: value }))}>
                    <SelectTrigger id="party-doc-type" className={fieldInput}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="CPF">CPF</SelectItem>
                      <SelectItem value="CNPJ">CNPJ</SelectItem>
                      <SelectItem value="RG">RG</SelectItem>
                      <SelectItem value="Outro">Outro</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="party-doc" className={fieldLabel}>Número do documento</Label>
                  <Input id="party-doc" value={newOpposingParty.document_number} onChange={(e) => setNewOpposingParty((p) => ({ ...p, document_number: e.target.value }))} placeholder={newOpposingParty.document_type === 'CNPJ' ? '00.000.000/0000-00' : '000.000.000-00'} className={fieldInput} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="party-email" className={fieldLabel}>Email</Label>
                  <Input id="party-email" type="email" value={newOpposingParty.email} onChange={(e) => setNewOpposingParty((p) => ({ ...p, email: e.target.value }))} placeholder="email@exemplo.com" className={fieldInput} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="party-phone" className={fieldLabel}>Telefone</Label>
                  <Input id="party-phone" value={newOpposingParty.phone} onChange={(e) => setNewOpposingParty((p) => ({ ...p, phone: e.target.value }))} placeholder="(81) 99999-9999" className={fieldInput} />
                </div>
                <div className="space-y-2 md:col-span-2">
                  <Label htmlFor="party-address" className={fieldLabel}>Endereço</Label>
                  <Textarea id="party-address" value={newOpposingParty.address} onChange={(e) => setNewOpposingParty((p) => ({ ...p, address: e.target.value }))} placeholder="Endereço completo" rows={2} className="rounded-lg border-slate-200 text-sm shadow-none placeholder:text-slate-400 focus-visible:ring-[#c4ab83]/30" />
                </div>
              </div>
              <div className="mt-4 flex justify-end">
                <Button onClick={handleCreateOpposingParty} disabled={creating || !newOpposingParty.name.trim()} className="rounded-xl bg-[#c4ab83] px-5 font-semibold text-slate-900 hover:bg-[#b39a72]">
                  {creating ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="mr-2 h-4 w-4" />}
                  Cadastrar e adicionar como principal
                </Button>
              </div>
            </section>
          )}

          {/* Lista de disponíveis */}
          <section className="rounded-2xl border border-slate-200/80">
            <div className="flex flex-col gap-2 border-b border-slate-100 p-3 sm:flex-row sm:items-center sm:justify-between">
              <p className="px-1 text-sm font-semibold text-slate-800">
                {activeTab === 'active' ? 'Clientes cadastrados' : 'Partes contrárias já cadastradas'}
              </p>
              <div className="relative sm:w-56">
                <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar..." className="h-8 rounded-lg border-0 bg-slate-50 pl-8 text-sm shadow-none focus-visible:ring-[#c4ab83]/30" />
              </div>
            </div>

            <div className="max-h-80 divide-y divide-slate-100 overflow-y-auto">
              {activeTab === 'active' &&
                (clients.length > 0 ? (
                  clients.map((client) => (
                    <div key={client.id} className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-[#faf7f2]/60">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#c4ab83] to-[#9a7f52] text-[11px] font-semibold text-white">
                          {initials(client.name)}
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-800">{client.name}</p>
                          {client.email && (
                            <p className="flex items-center gap-1 truncate text-xs text-slate-500"><Mail className="h-3 w-3" /> {client.email}</p>
                          )}
                        </div>
                      </div>
                      <ActionButtons id={client.id} onAdd={handleAddClient} />
                    </div>
                  ))
                ) : (
                  <div className="px-6 py-10 text-center">
                    <Users className="mx-auto h-5 w-5 text-[#c4ab83]" />
                    <p className="mt-2 text-sm text-slate-500">{term ? 'Nenhum cliente com esse nome.' : 'Todos os clientes já estão neste processo.'}</p>
                  </div>
                ))}

              {activeTab === 'passive' &&
                (parties.length > 0 ? (
                  parties.map((party) => (
                    <div key={party.id} className="flex items-center justify-between gap-3 px-4 py-2.5 hover:bg-[#faf7f2]/60">
                      <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100">
                          <Building2 className="h-4 w-4 text-slate-500" />
                        </span>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-slate-800">{party.name}</p>
                          <p className="flex flex-wrap gap-x-3 text-xs text-slate-500">
                            {party.document_number && <span className="flex items-center gap-1"><FileText className="h-3 w-3" /> {party.document_number}</span>}
                            {party.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {party.phone}</span>}
                            {party.email && <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {party.email}</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex shrink-0 items-center gap-1.5">
                        <ActionButtons id={party.id} onAdd={handleAddOpposingParty} />
                        <button
                          type="button"
                          onClick={() => handleDeleteOpposingParty(party)}
                          disabled={!!busyId}
                          className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                          title="Excluir parte contrária"
                        >
                          {busyId === `${party.id}-delete` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                        </button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="px-6 py-10 text-center">
                    <Building2 className="mx-auto h-5 w-5 text-[#c4ab83]" />
                    <p className="mt-2 text-sm text-slate-500">{term ? 'Nenhuma parte com esse nome.' : 'Nenhuma parte contrária cadastrada ainda. Use o formulário acima.'}</p>
                  </div>
                ))}
            </div>
          </section>
        </div>
      </DialogContent>
    </Dialog>
  );
};
